import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RiBox3Line } from 'react-icons/ri';
import { MdInventory } from 'react-icons/md';
import { IoTrendingDown, IoAnalyticsOutline } from 'react-icons/io5';
import { IoIosReturnLeft } from 'react-icons/io';
import { FiDownload } from 'react-icons/fi';
import toast from '../components/common/toast';
import Table from '../components/common/Table';
import SkeletonTable from '../components/SkeletonTable';
import { reportService, companyService } from '../services/api';
import { exportPalletStorageReportPdfFromExcelBlob } from '../utils/exportPalletStorageReportPdf';
import { exportCustomerMisPalletReportPdfFromExcelBlob } from '../utils/exportCustomerMisPalletReportPdf';
import ReportDetailsModal from '../components/common/ReportDetailsModal';
import {
 TotalProductsIcon,
 TotalBinsIcon,
 TotalProductsIcon as TotalUOMIcon, // agar alag UOM icon nahi hai to ye use kar lo
} from "../assets/states/StatsIcons";

const SummaryCard = ({
  label,
  value,
  Icon,
  bg,
  stroke = true,
  icon,
  iconBgColor = 'bg-gray-100',
  iconTextColor = 'text-gray-600',
}) => {
  const hasIconComponent = Icon && typeof Icon === 'function';
  const hasIconElement = icon != null && React.isValidElement(icon);

  const boxStyle = {
    width: 40,
    height: 40,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 20,
    borderBottomLeftRadius: 6,
    ...(hasIconComponent && bg ? { background: bg } : {}),
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm w-full min-w-0">
      <div className="flex items-center gap-5">
        <div
          className={`flex items-center justify-center flex-shrink-0 ${hasIconElement ? iconBgColor : ''}`}
          style={boxStyle}
        >
          {hasIconComponent && (stroke ? <Icon size={18} color="#FFFFFF" /> : <Icon size={24} />)}
          {hasIconElement && <span className={iconTextColor}>{icon}</span>}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-semibold text-gray-900 leading-none">{value}</p>
          <p className="mt-2 text-sm font-medium text-gray-400 whitespace-nowrap truncate">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
};


const REPORT_TABS = [
  { id: 'product', label: 'Inventory by Product' },
  { id: 'batch', label: 'Inventory by Batch' },
  // { id: 'uom', label: 'Inventory by UOM' },
  // { id: 'summary', label: 'Inventory by Summary' },
  { id: 'aging', label: 'Ageing Report' },
  { id: 'pallet', label: 'Pallet Reports' },
];

const PALLET_SUB_TABS = [
  { id: 'storageVisibility', label: 'Pallet Storage Visibility' },
  { id: 'customerMis', label: 'Customer MIS Pallet' },
];

const formatPalletReportNumber = (val) => {
  if (val == null || val === '') return '—';
  const n = Number(val);
  return Number.isFinite(n) ? n : '—';
};

const PALLET_STORAGE_MOVEMENT_COLUMNS = [
  { key: 'casesOpening', label: 'Opening', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'casesInward', label: 'Inward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'casesOutward', label: 'Outward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'casesClosing', label: 'Closing', align: 'right', render: (val) => formatPalletReportNumber(val) },
  {
    key: 'casesOpeningPlusInward',
    label: 'Opening + Inward',
    align: 'right',
    render: (val) => formatPalletReportNumber(val),
  },
  { key: 'palletsOpening', label: 'Opening', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'palletsInward', label: 'Inward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'palletsOutward', label: 'Outward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'palletsClosing', label: 'Closing', align: 'right', render: (val) => formatPalletReportNumber(val) },
  {
    key: 'palletsOpeningPlusInward',
    label: 'Opening + Inward',
    align: 'right',
    render: (val) => formatPalletReportNumber(val),
  },
];

const PALLET_STORAGE_COLUMNS = [
  { key: 'customer', label: 'Customer' },
  { key: 'skuCode', label: 'SKU Code' },
  { key: 'skuName', label: 'SKU Name' },
  { key: 'uom', label: 'UOM', align: 'center' },
  { key: 'conversionQtyPerPallet', label: 'Conversion Qty Per Pallet', align: 'right' },
  { key: 'currentUnitsStock', label: 'Current Units Stock', align: 'right' },
  { key: 'currentPalletStock', label: 'Current Pallet Stock', align: 'right' },
  { key: 'weightPerPallet', label: 'Weight Per Pallet', align: 'right' },
  {
    key: 'date',
    label: 'Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  ...PALLET_STORAGE_MOVEMENT_COLUMNS,
];

const PALLET_STORAGE_GROUPED_HEADERS = {
  baseColumnCount: 9,
  groups: [
    { label: 'CASES', span: 5 },
    { label: 'PALLETS', span: 5 },
  ],
};

const PALLET_CUSTOMER_MIS_COLUMNS = [
  { key: 'customer', label: 'Customer' },
  { key: 'sku', label: 'SKU' },
  { key: 'skuName', label: 'SKU Name' },
  { key: 'receivedUnits', label: 'Received Units', align: 'right' },
  { key: 'receivedPalletEquivalent', label: 'Received Pallet Equivalent', align: 'right' },
  { key: 'currentUnitsStock', label: 'Current Units Stock', align: 'right' },
  { key: 'currentPalletStock', label: 'Current Pallet Stock', align: 'right' },
  { key: 'dispatchedUnits', label: 'Dispatched Units', align: 'right' },
  { key: 'dispatchedPalletEquivalent', label: 'Dispatched Pallet Equivalent', align: 'right' },
  { key: 'conversionQtyPerPallet', label: 'Conversion Qty Per Pallet', align: 'right' },
  { key: 'weightPerPallet', label: 'Weight Per Pallet', align: 'right' },
];

const STORAGE_TYPE_OPTIONS = [
  { value: '', label: 'All Storage Types' },
  { value: 'FROZEN', label: 'Frozen' },
  { value: 'CHILLED', label: 'Chilled' },
  { value: 'TROPICAL', label: 'Tropical' },
  { value: 'DRIED', label: 'Dried' },
];

const PRODUCT_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'SKU Name' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'uom', label: 'UOM' },
  { key: 'openingStock', label: 'Opening', align: 'right' },
  { key: 'inwardQuantity', label: 'Inward', align: 'right' },
  { key: 'outwardQuantity', label: 'Outward', align: 'right' },
  { key: 'returnQuantity', label: 'Return', align: 'right' },
  { key: 'closingStock', label: 'Closing', align: 'right' },
  // { key: 'availableQuantity', label: 'Available', align: 'right' },
  // { key: 'goodQuantity', label: 'GOOD', align: 'right' },
  // { key: 'holdQuantity', label: 'HOLD', align: 'right' },
];

const Pill = ({ text, variant = "blue" }) => {
 const map = {
   green: "border-emerald-500 text-emerald-600 bg-emerald-50/50",
   orange: "border-orange-300 text-orange-400 bg-orange-50/50",
   blue: "border-indigo-800 text-indigo-900 bg-indigo-50/40",
   gray: "border-gray-300 text-gray-700 bg-gray-50",
 };

 return (
   <span
     className={`inline-flex items-center justify-center rounded-full border px-4 py-1 text-sm font-medium whitespace-nowrap ${map[variant] || map.gray}`}
   >
     {text}
   </span>
 );
};

const BATCH_COLUMNS = [
 { key: 'productCode', label: 'Product Code' },
 { 
   key: 'companyName', 
   label: 'Company',
   render: (val) => val || '—'
 },
 { key: 'batchNumber', label: 'Batch Number' },
 {
   key: 'manufacturingDate',
   label: 'Manufacturing Date',
   render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
 },
 {
   key: 'expiryDate',
   label: 'Exp Date',
   render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
 },
 { key: 'quantity', label: 'Quantity', align: 'right' },

 // ✅ condition pill (Expired / Good)
 {
   key: 'condition',
   label: 'Condition',
   render: (val) => {
     const v = String(val || '').toLowerCase();
     const isExpired = v.includes('expired');
     const isGood = v.includes('good');
     return (
       <Pill
         text={val || '—'}
         variant={isExpired ? 'orange' : isGood ? 'green' : 'gray'}
       />
     );
   },
 },

 // { key: 'binCode', label: 'Bin Code' },

 // ✅ binType pill (blue)
 // {
 //   key: 'binType',
 //   label: 'Bin Type',
 //   render: (val) => <Pill text={val || '—'} variant="blue" />,
 // },
];


// const UOM_COLUMNS = [
//   { key: 'uom', label: 'UOM' },
//   { key: 'productCode', label: 'Product Code' },
//   { key: 'productDescription', label: 'Description' },
//   { 
//     key: 'companyName', 
//     label: 'Company',
//     render: (val) => val || '—'
//   },
//   { key: 'qnt', label: 'Qnt', align: 'right' },
//   { key: 'holdQty', label: 'Hold Qnt', align: 'right' },
// ];

const EXPIRED_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'manufacturingDate',
    label: 'Manufacturing Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  // { 
  //   key: 'shelfLifeRemainingPercent', 
  //   label: '% Remaining',
  //   align: 'right',
  //   render: (val) => val != null ? `${val}%` : '—'
  // },
  { key: 'quantity', label: 'Quantity', align: 'right' },
  // { key: 'binCode', label: 'Bin Code' },
];

const NEAR_EXPIRY_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'manufacturingDate',
    label: 'Manufacturing Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  { 
    key: 'shelfLifeRemainingPercent', 
    label: '% Remaining',
    align: 'right',
    render: (val) => val != null ? `${val}%` : '—'
  },
  { key: 'daysUntilExpiry', label: 'Days Left', align: 'right' },
  { key: 'quantity', label: 'Quantity', align: 'right' },
];

const CARD_TITLES = {
  product: 'Inventory Report (Product)',
  batch: 'Inventory Report (Batch)',
  // uom: 'Inventory Report (UOM)',
  // summary: 'Inventory Summary',
  aging: 'Ageing Report',
  pallet: 'Pallet Reports',
};

const PRODUCT_DETAIL_FIELDS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'SKU Name' },
  { key: 'companyName', label: 'Company' },
  { key: 'uom', label: 'UOM' },
  { key: 'openingStock', label: 'Opening Stock' },
  { key: 'inwardQuantity', label: 'Inward Quantity' },
  { key: 'outwardQuantity', label: 'Outward Quantity' },
  { key: 'returnQuantity', label: 'Return Quantity' },
  { key: 'closingStock', label: 'Closing Stock' },
  { key: 'availableQuantity', label: 'Available Quantity' },
  { key: 'goodQuantity', label: 'GOOD Quantity' },
  { key: 'holdQuantity', label: 'HOLD Quantity' },
  { key: 'batchCount', label: 'Batch Count' },
  { key: 'reportStartDate', label: 'Report Start Date' },
  { key: 'reportEndDate', label: 'Report End Date' },
];

const BATCH_DETAIL_FIELDS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'Description' },
  { key: 'skuName', label: 'SKU Name' },
  { key: 'companyName', label: 'Company' },
  { key: 'batchNumber', label: 'Batch Number' },
  { key: 'manufacturingDate', label: 'Manufacturing Date', format: 'date' },
  { key: 'expiryDate', label: 'Expiry Date', format: 'date' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'condition', label: 'Condition' },
  { key: 'binCode', label: 'Bin Code' },
  { key: 'binType', label: 'Bin Type' },
  { key: 'uom', label: 'UOM' },
  { key: 'daysUntilExpiry', label: 'Days Until Expiry' },
  { key: 'shelfLifeRemainingPercent', label: 'Shelf Life Remaining %' },
  { key: 'shelfLifeUsedPercent', label: 'Shelf Life Used %' },
];

function buildDetailFields(row, fieldDefs) {
  if (!row || typeof row !== 'object') return [];
  const seen = new Set();
  const fields = [];

  for (const def of fieldDefs) {
    if (!(def.key in row)) continue;
    seen.add(def.key);
    fields.push({
      key: def.key,
      label: def.label,
      value: row[def.key],
      format: def.format,
    });
  }

  // Surface any remaining simple scalar fields from the row object.
  for (const [key, value] of Object.entries(row)) {
    if (seen.has(key)) continue;
    if (value != null && typeof value === 'object') continue;
    const label = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
    fields.push({ key, label, value });
  }

  return fields;
}

export function Reports() {
  const [activeReport, setActiveReport] = useState('product');
  const [palletSubReport, setPalletSubReport] = useState('storageVisibility');
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [page, setPage] = useState(1);
  const limit = null;
  const batchReportPageSize = 20;
  const [searchFilter] = useState('');
  const [skuDraft, setSkuDraft] = useState('');
  const [binDraft, setBinDraft] = useState('');
  const [storageTypeDraft, setStorageTypeDraft] = useState('');
  const [startDateDraft, setStartDateDraft] = useState('');
  const [endDateDraft, setEndDateDraft] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    companyId: '',
    search: '',
    sku: '',
    binCode: '',
    storageType: '',
    startDate: '',
    endDate: '',
  });
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    title: '',
    fields: [],
  });

  // LEGACY: auto-applied debounced filters on every date keystroke.
  // Disabled: product report dates apply via Search; company filter still applies immediately.
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setDebouncedFilters({
  //       companyId: selectedCompanyId,
  //       search: searchFilter,
  //       startDate: startDateFilter,
  //       endDate: endDateFilter,
  //     });
  //     setPage(1);
  //   }, 400);
  //   return () => clearTimeout(timer);
  // }, [selectedCompanyId, searchFilter, startDateFilter, endDateFilter]);

  const handleCompanyChange = (companyId) => {
    setSelectedCompanyId(companyId);
    setAppliedFilters((prev) => ({ ...prev, companyId }));
    setPage(1);
  };

  // LEGACY: manual Search button applied draft dates to appliedFilters.
  // const handleApplyDateSearch = () => {
  //   const start = startDateDraft || todayYmdIST();
  //   const end = endDateDraft || start;
  //   setStartDateDraft(start);
  //   setEndDateDraft(end);
  //   setAppliedFilters((prev) => ({
  //     ...prev,
  //     startDate: start,
  //     endDate: end,
  //   }));
  //   setPage(1);
  // };

  const applyDateFilters = useCallback((startDraft, endDraft) => {
    const start = startDraft || '';
    const end = endDraft || (startDraft ? startDraft : '');
    setAppliedFilters((prev) => {
      if (prev.startDate === start && prev.endDate === end) return prev;
      return { ...prev, startDate: start, endDate: end };
    });
    setPage(1);
  }, []);

  useEffect(() => {
    if (activeReport !== 'product' && activeReport !== 'pallet') return undefined;
    const timer = setTimeout(() => {
      applyDateFilters(startDateDraft, endDateDraft);
    }, 400);
    return () => clearTimeout(timer);
  }, [startDateDraft, endDateDraft, activeReport, applyDateFilters]);

  useEffect(() => {
    if (activeReport !== 'pallet') return undefined;
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => {
        const nextSku = skuDraft.trim();
        const nextBin = binDraft.trim();
        const nextStorage = storageTypeDraft;
        if (
          prev.sku === nextSku &&
          prev.binCode === nextBin &&
          prev.storageType === nextStorage
        ) {
          return prev;
        }
        return {
          ...prev,
          sku: nextSku,
          binCode: nextBin,
          storageType: nextStorage,
        };
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [skuDraft, binDraft, storageTypeDraft, activeReport]);

  useEffect(() => {
    setPage(1);
  }, [palletSubReport]);

  const hasActiveFilters = useMemo(() => {
    return (
      (selectedCompanyId || '') !== '' ||
      (startDateDraft || '') !== '' ||
      (endDateDraft || '') !== '' ||
      (skuDraft || '') !== '' ||
      (binDraft || '') !== '' ||
      (storageTypeDraft || '') !== ''
    );
  }, [selectedCompanyId, startDateDraft, endDateDraft, skuDraft, binDraft, storageTypeDraft]);

  const handleClearFilters = () => {
    setSelectedCompanyId('');
    setStartDateDraft('');
    setEndDateDraft('');
    setSkuDraft('');
    setBinDraft('');
    setStorageTypeDraft('');
    setAppliedFilters((prev) => ({
      ...prev,
      companyId: '',
      startDate: '',
      endDate: '',
      sku: '',
      binCode: '',
      storageType: '',
    }));
    setPage(1);
  };

  const closeDetailsModal = useCallback(() => {
    setDetailsModal({ open: false, title: '', fields: [] });
  }, []);

  const openProductDetails = useCallback((row) => {
    setDetailsModal({
      open: true,
      title: 'Product Details',
      fields: buildDetailFields(row, PRODUCT_DETAIL_FIELDS),
    });
  }, []);

  const openBatchDetails = useCallback((row) => {
    setDetailsModal({
      open: true,
      title: 'Batch Details',
      fields: buildDetailFields(row, BATCH_DETAIL_FIELDS),
    });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [activeReport]);

  const reportFilters = useMemo(() => {
    const filters = {
      companyId: appliedFilters.companyId || undefined,
      search: appliedFilters.search?.trim() || undefined,
    };
    if (activeReport === 'product' || activeReport === 'pallet') {
      if (appliedFilters.startDate) filters.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) filters.endDate = appliedFilters.endDate;
    }
    if (activeReport === 'pallet') {
      if (appliedFilters.sku) filters.sku = appliedFilters.sku;
      if (palletSubReport === 'storageVisibility') {
        if (appliedFilters.binCode) filters.binCode = appliedFilters.binCode;
        if (appliedFilters.storageType) filters.storageType = appliedFilters.storageType;
      }
    }
    return filters;
  }, [appliedFilters, activeReport, palletSubReport]);

  const getPalletExportParams = () => {
    const companyId = appliedFilters.companyId || undefined;
    const palletParams = {
      companyId,
      sku: appliedFilters.sku || undefined,
    };
    if (appliedFilters.startDate) palletParams.startDate = appliedFilters.startDate;
    if (appliedFilters.endDate) palletParams.endDate = appliedFilters.endDate;
    if (palletSubReport === 'storageVisibility') {
      if (appliedFilters.binCode) palletParams.binCode = appliedFilters.binCode;
      if (appliedFilters.storageType) palletParams.storageType = appliedFilters.storageType;
    }
    return palletParams;
  };

  const handleDownloadReport = async (reportType) => {
    try {
      setDownloading(true);
      let blob;
      let filename;
      const companyId = appliedFilters.companyId || undefined;
      const dateParams = { companyId };
      if (appliedFilters.startDate) dateParams.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) dateParams.endDate = appliedFilters.endDate;
      const today = new Date().toISOString().split('T')[0];

      switch (reportType) {
        case 'product':
          blob = await reportService.downloadInventoryReportByProduct(dateParams);
          filename = `inventory_report_by_product_${today}.xlsx`;
          break;
        case 'batch':
          blob = await reportService.downloadInventoryReportByBatch(companyId);
          filename = `inventory_report_by_batch_${today}.xlsx`;
          break;
        case 'uom':
          blob = await reportService.downloadInventoryReportByUOM(companyId);
          filename = `inventory_report_by_uom_${today}.xlsx`;
          break;
        case 'summary':
          blob = await reportService.downloadInventorySummary(companyId);
          filename = `inventory_summary_${today}.xlsx`;
          break;
        case 'aging':
          blob = await reportService.downloadAgingReport(companyId);
          filename = `aging_report_${today}.xlsx`;
          break;
        case 'pallet': {
          const palletParams = getPalletExportParams();
          if (palletSubReport === 'storageVisibility') {
            blob = await reportService.downloadPalletStorageVisibilityReport(palletParams);
            filename = `pallet_storage_visibility_${today}.xlsx`;
          } else {
            blob = await reportService.downloadCustomerMisPalletReport(palletParams);
            filename = `customer_mis_pallet_${today}.xlsx`;
          }
          break;
        }
        default:
          throw new Error('Invalid report type');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error?.message || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPalletPdf = async () => {
    try {
      setDownloadingPdf(true);
      const today = new Date().toISOString().split('T')[0];
      const palletParams = getPalletExportParams();
      let blob;
      let filename;

      if (palletSubReport === 'storageVisibility') {
        blob = await reportService.downloadPalletStorageVisibilityReport(palletParams);
        filename = `pallet_storage_visibility_${today}.pdf`;
        await exportPalletStorageReportPdfFromExcelBlob(blob, filename);
      } else {
        blob = await reportService.downloadCustomerMisPalletReport(palletParams);
        filename = `customer_mis_pallet_${today}.pdf`;
        await exportCustomerMisPalletReportPdfFromExcelBlob(blob, filename);
      }

      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading pallet PDF:', error);
      toast.error(error?.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getReports = useCallback(async ({ page: pageArg, limit: limitArg, filters }) => {
    if (activeReport === 'product') {
      return reportService.getInventoryReportByProduct(filters);
    }
    const params = {
      page: pageArg,
      limit: limitArg ?? batchReportPageSize,
      ...filters,
    };
    if (activeReport === 'batch') return reportService.getInventoryReportByBatch(params);
    if (activeReport === 'aging') return reportService.getAgingReport(params);
    if (activeReport === 'pallet') {
      if (palletSubReport === 'storageVisibility') {
        return reportService.getPalletStorageVisibilityReport(params);
      }
      return reportService.getCustomerMisPalletReport(params);
    }
    return { success: true, data: [] };
  }, [activeReport, batchReportPageSize, palletSubReport]);

  // const fetchInventoryByUOM = async () => {
  //   try {
  //     setLoading(true);
  //     const companyId = selectedCompanyId || undefined;
  //     const result = await reportService.getInventoryReportByUOM(companyId);
  //     if (result && result.success && Array.isArray(result.data)) {
  //       const flattened = [];
  //       for (const group of result.data) {
  //         for (const p of group.products || []) {
  //           flattened.push({
  //             uom: group.uom,
  //             productCode: p.productCode,
  //             productDescription: p.productDescription,
  //             companyName: p.companyName || '—',
  //             qnt: p.qnt || 0,
  //             holdQty: p.holdQty || 0,
  //           });
  //         }
  //       }
  //       setUomData(flattened);
  //     } else {
  //       setUomData([]);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching inventory by UOM:', error);
  //     setUomData([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const fetchInventorySummary = async () => {
  //   try {
  //     setLoading(true);
  //     const companyId = selectedCompanyId || undefined;
  //     const result = await reportService.getInventorySummary(companyId);
  //     if (result && result.success && result.summary) {
  //       setSummaryData(result.summary);
  //     } else {
  //       setSummaryData(null);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching inventory summary:', error);
  //     setSummaryData(null);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const { data: companiesResponse } = useQuery({
    queryKey: ['reportCompanies'],
    queryFn: () => companyService.getCompanies({ isActive: 'true' }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const companies = useMemo(() => companiesResponse?.data || [], [companiesResponse]);

  const {
    data: reportResponse,
    isLoading,
    isFetching,
    isError: isReportError,
    error: reportError,
  } = useQuery({
    queryKey:
      activeReport === 'product'
        ? ['reports', activeReport, reportFilters]
        : ['reports', activeReport, palletSubReport, page, batchReportPageSize, reportFilters],
    queryFn: () =>
      getReports({
        page,
        limit: activeReport === 'product' ? limit : batchReportPageSize,
        filters: reportFilters,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const loading = isLoading || isFetching;
  const productData = useMemo(
    () => (activeReport === 'product' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const batchData = useMemo(
    () => (activeReport === 'batch' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const palletData = useMemo(
    () => (activeReport === 'pallet' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const expiredBatches = useMemo(
    () => (activeReport === 'aging' && Array.isArray(reportResponse?.expiredBatches) ? reportResponse.expiredBatches : []),
    [activeReport, reportResponse]
  );
  const nearExpiryBatches = useMemo(
    () => (activeReport === 'aging' && Array.isArray(reportResponse?.nearExpiryBatches) ? reportResponse.nearExpiryBatches : []),
    [activeReport, reportResponse]
  );
  const ageingSummary = useMemo(
    () => (activeReport === 'aging' ? reportResponse?.summary || null : null),
    [activeReport, reportResponse]
  );
  const totalCount = Number(reportResponse?.count || 0);
  const hasNextPage =
    activeReport !== 'product' && totalCount > page * batchReportPageSize;

  return (
    <div
      className="w-full pl-3 pr-6 pt-2 pb-6 bg-gray-50 min-h-screen"
      style={{ minWidth: '800px', fontFamily: "'Nunito', sans-serif" }}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-4 w-full"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #EEF1F5',
          borderRadius: 28,
          padding: '16px 24px',
          boxShadow: '0px 0px 4px 0px #959DA526',
        }}
      >
        <h1
          style={{
            fontFamily: 'Nunito',
            fontSize: 18,
            fontWeight: 600,
            color: '#161616',
            margin: 0,
          }}
        >
          Reports
        </h1>
        <div
          className="overflow-x-auto"
          style={{
            border: '1px solid #D0D5DD',
            borderRadius: 14,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            padding: 6,
            gap: 6,
          }}
        >
          {REPORT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveReport(tab.id)}
              className="flex-shrink-0 whitespace-nowrap transition"
              style={{
                background: activeReport === tab.id ? 'rgba(24,135,66,0.08)' : 'transparent',
                color: activeReport === tab.id ? '#188742' : '#101828',
                fontWeight: activeReport === tab.id ? 600 : undefined,
                borderRadius: 12,
                padding: '10px 18px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 w-full rounded-2xl p-6 border border-[#18181B0F] bg-[#fcfcfc]">
      <div
        className={
          activeReport === 'pallet'
            ? 'mb-4 overflow-x-auto'
            : 'mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'
        }
        style={
          activeReport === 'pallet'
            ? {
                backgroundColor: '#FFFFFF',
                border: '1px solid #EEF1F5',
                borderRadius: 16,
                padding: '10px 14px',
                boxShadow: '0px 0px 4px 0px #959DA526',
              }
            : undefined
        }
      >
  {activeReport !== 'pallet' && (
  <h2 className="text-lg font-semibold text-gray-900 shrink-0">
    {CARD_TITLES[activeReport] || CARD_TITLES.product}
  </h2>
  )}

  <div
    className={
      activeReport === 'pallet'
        ? 'flex flex-nowrap items-center gap-2 min-w-max'
        : 'flex flex-wrap items-center gap-3 lg:justify-end min-w-0'
    }
  >
    {(activeReport === 'product' || activeReport === 'pallet') && (
      <>
        <label
          className={`flex items-center text-sm text-gray-600 shrink-0 ${
            activeReport === 'pallet' ? 'gap-1.5' : 'gap-2'
          }`}
        >
          <span className="whitespace-nowrap font-medium">From</span>
          <input
            type="date"
            value={startDateDraft}
            onChange={(e) => setStartDateDraft(e.target.value)}
            className={`rounded-[12px] border border-[#D0D5DD] bg-white text-sm text-[#101828] shrink-0 ${
              activeReport === 'pallet' ? 'px-2 w-[124px]' : 'px-3 w-[148px]'
            }`}
            style={{ height: 44 }}
            title="From Date (IST)"
          />
        </label>
        <label
          className={`flex items-center text-sm text-gray-600 shrink-0 ${
            activeReport === 'pallet' ? 'gap-1.5' : 'gap-2'
          }`}
        >
          <span className="whitespace-nowrap font-medium">To</span>
          <input
            type="date"
            value={endDateDraft}
            onChange={(e) => setEndDateDraft(e.target.value)}
            className={`rounded-[12px] border border-[#D0D5DD] bg-white text-sm text-[#101828] shrink-0 ${
              activeReport === 'pallet' ? 'px-2 w-[124px]' : 'px-3 w-[148px]'
            }`}
            style={{ height: 44 }}
            title="To Date (IST)"
          />
        </label>
      </>
    )}
    {activeReport === 'pallet' && (
      <>
        <input
          type="text"
          value={skuDraft}
          onChange={(e) => setSkuDraft(e.target.value)}
          placeholder="SKU"
          className="rounded-[12px] border border-[#D0D5DD] bg-white px-2 text-sm text-[#101828] w-[96px] shrink-0"
          style={{ height: 44 }}
        />
        {palletSubReport === 'storageVisibility' && (
          <>
            <input
              type="text"
              value={binDraft}
              onChange={(e) => setBinDraft(e.target.value)}
              placeholder="Bin"
              className="rounded-[12px] border border-[#D0D5DD] bg-white px-2 text-sm text-[#101828] w-[80px] shrink-0"
              style={{ height: 44 }}
            />
            <select
              value={storageTypeDraft}
              onChange={(e) => setStorageTypeDraft(e.target.value)}
              className="rounded-[12px] border border-[#D0D5DD] bg-white px-2 text-sm text-[#101828] shrink-0"
              style={{ height: 44, width: 140 }}
            >
              {STORAGE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </>
        )}
      </>
    )}
    <label className="flex items-center gap-2 text-sm text-gray-600 shrink-0">
      <div className="relative flex items-center">
        <select
          value={selectedCompanyId}
          onChange={(e) => handleCompanyChange(e.target.value)}
          className={`rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold appearance-none cursor-pointer transition hover:bg-[#188742]/5 disabled:opacity-50 disabled:cursor-not-allowed truncate shrink-0 ${
            activeReport === 'pallet' ? 'pr-8' : 'pr-9 max-w-[200px]'
          }`}
          style={{
            height: 44,
            paddingLeft: activeReport === 'pallet' ? 12 : 14,
            paddingRight: activeReport === 'pallet' ? 28 : 32,
            minWidth: activeReport === 'pallet' ? 120 : 132,
            width: activeReport === 'pallet' ? 128 : 148,
          }}
          title={
            selectedCompanyId
              ? companies.find((c) => String(c._id || c.id) === String(selectedCompanyId))?.name
              : 'All Companies'
          }
        >
          <option value="">All Companies</option>
          {companies.map((company) => (
            <option key={company._id || company.id} value={company._id || company.id}>
              {company.companyCode ? `${company.companyCode} - ${company.name}` : company.name}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-2.5 flex items-center text-[#188742]" style={{ top: '50%', transform: 'translateY(-50%)' }}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </span>
      </div>
    </label>

    {hasActiveFilters && (
      <button
        type="button"
        onClick={handleClearFilters}
        disabled={loading}
        className="rounded-[12px] border border-[#D0D5DD] bg-white text-[#344054] font-semibold transition hover:bg-gray-50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
        style={{
          height: 44,
          paddingLeft: activeReport === 'pallet' ? 12 : 16,
          paddingRight: activeReport === 'pallet' ? 12 : 16,
        }}
      >
        Clear
      </button>
    )}

    <button
      type="button"
      onClick={() => handleDownloadReport(activeReport)}
      disabled={downloading || downloadingPdf || loading}
      className="flex items-center gap-2 rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
      style={{
        height: 44,
        paddingLeft: activeReport === 'pallet' ? 14 : 18,
        paddingRight: activeReport === 'pallet' ? 14 : 18,
      }}
    >
      <FiDownload className="w-4 h-4 shrink-0" />
      {downloading
        ? "Downloading..."
        : activeReport === "pallet"
          ? "Export Excel"
          : "Export CSV"}
    </button>
    {activeReport === 'pallet' && (
      <button
        type="button"
        onClick={handleDownloadPalletPdf}
        disabled={downloading || downloadingPdf || loading}
        className="flex items-center gap-2 rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
        style={{
          height: 44,
          paddingLeft: 14,
          paddingRight: 14,
        }}
      >
        <FiDownload className="w-4 h-4 shrink-0" />
        {downloadingPdf ? 'Downloading...' : 'Export PDF'}
      </button>
    )}
    {/* TODO: Enable CSV export for Pallet Reports in future if business requires it
    <button type="button" onClick={() => handleDownloadReport('pallet', 'csv')}>
      Export CSV
    </button>
    */}
  </div>
</div>

        {activeReport === 'pallet' && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {PALLET_SUB_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPalletSubReport(tab.id)}
                className="rounded-[10px] px-4 py-2 text-sm transition"
                style={{
                  background: palletSubReport === tab.id ? 'rgba(24,135,66,0.08)' : '#FFFFFF',
                  color: palletSubReport === tab.id ? '#188742' : '#101828',
                  fontWeight: palletSubReport === tab.id ? 600 : 500,
                  border: '1px solid #D0D5DD',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}


        {activeReport === 'product' && (
          loading ? (
            <SkeletonTable cols={PRODUCT_COLUMNS.length} rows={8} />
          ) : (
            <Table
              columns={PRODUCT_COLUMNS}
              data={productData}
              loading={false}
              emptyMessage="No data available"
              onRowClick={openProductDetails}
            />
          )
        )}

        {activeReport === 'batch' && (
          loading ? (
            <SkeletonTable cols={BATCH_COLUMNS.length} rows={8} />
          ) : (
            <Table
              columns={BATCH_COLUMNS}
              data={batchData}
              loading={false}
              emptyMessage="No data available"
              onRowClick={openBatchDetails}
            />
          )
        )}

        {/* {activeReport === 'uom' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
  <SummaryCard
    label="Total Products Out of Stock"
    value={uomData.filter((r) => (r.qnt ?? 0) === 0).length}
    Icon={TotalProductsIcon}
    bg="#DC2626"   // red
    stroke={true}
  />

  <SummaryCard
    label="Total Products (in report)"
    value={uomData.length}
    Icon={TotalBinsIcon}
    bg="#01C36F"   // green
    stroke={true}
  />

  <SummaryCard
    label="Total UOMs"
    value={[...new Set(uomData.map((r) => r.uom))].length}
    Icon={TotalProductsIcon}
    bg="#4F46E5"   // blue/purple
    stroke={true}
  />
</div>

                <div className="mt-8">
                  <Table
                    columns={UOM_COLUMNS}
                    data={uomData}
                    loading={loading}
                    emptyMessage="No data available"
                  />
                </div>
              </>
            )}
          </>
        )} */}

        {/* {activeReport === 'summary' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : summaryData ? (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                <SummaryCard
                  label="Total Products"
                  value={summaryData.totalProducts ?? 0}
                  icon={<RiBox3Line className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Opening Stock"
                  value={summaryData.totalOpeningStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Inward"
                  value={summaryData.totalInwardQuantity ?? 0}
                  icon={<IoTrendingDown className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Outward"
                  value={summaryData.totalOutwardQuantity ?? 0}
                  icon={<IoAnalyticsOutline className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Return"
                  value={summaryData.totalReturnQuantity ?? 0}
                  icon={<IoIosReturnLeft className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Closing Stock"
                  value={summaryData.totalClosingStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-gray-500">No data available</div>
            )}
          </>
        )} */}

        {activeReport === 'aging' && (
          <div className="space-y-8">
            {loading ? (
              <>
                <SkeletonTable cols={EXPIRED_COLUMNS.length} rows={5} />
                <SkeletonTable cols={NEAR_EXPIRY_COLUMNS.length} rows={5} />
              </>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                  <SummaryCard
                    label="Expired Batches"
                    value={ageingSummary?.expiredBatchCount ?? expiredBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-red-50"
                    iconTextColor="text-red-600"
                  />
                  <SummaryCard
                    label="Near Expiry (30 Days)"
                    value={ageingSummary?.nearExpiryBatchCount ?? nearExpiryBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-amber-50"
                    iconTextColor="text-amber-600"
                  />
                  <SummaryCard
                    label="Good Batches"
                    value={ageingSummary?.goodBatchCount ?? 0}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-emerald-50"
                    iconTextColor="text-emerald-600"
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Expired Batches</h3>
                  <Table
                    columns={EXPIRED_COLUMNS}
                    data={expiredBatches}
                    loading={loading}
                    emptyMessage="No expired batches"
                    onRowClick={openBatchDetails}
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Near Expiry Batches (Next 30 Days)</h3>
                  <Table
                    columns={NEAR_EXPIRY_COLUMNS}
                    data={nearExpiryBatches}
                    loading={loading}
                    emptyMessage="No near expiry batches"
                    onRowClick={openBatchDetails}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {activeReport === 'pallet' && (
          <>
            {isReportError ? (
              <div className="py-12 text-center text-sm text-red-600">
                {reportError?.message || 'Failed to load pallet report'}
              </div>
            ) : loading ? (
              <SkeletonTable
                cols={
                  palletSubReport === 'storageVisibility'
                    ? PALLET_STORAGE_COLUMNS.length
                    : PALLET_CUSTOMER_MIS_COLUMNS.length
                }
                rows={8}
              />
            ) : (
              <Table
                columns={
                  palletSubReport === 'storageVisibility'
                    ? PALLET_STORAGE_COLUMNS
                    : PALLET_CUSTOMER_MIS_COLUMNS
                }
                groupedHeaders={
                  palletSubReport === 'storageVisibility'
                    ? PALLET_STORAGE_GROUPED_HEADERS
                    : null
                }
                data={palletData}
                loading={false}
                emptyMessage="No pallet data available"
              />
            )}
          </>
        )}
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page <= 1 || loading}
            className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">Page {page}</span>
          <button
            type="button"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={loading || !hasNextPage}
            className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      <ReportDetailsModal
        open={detailsModal.open}
        onClose={closeDetailsModal}
        title={detailsModal.title}
        fields={detailsModal.fields}
      />
    </div>
  );
}
